/**
 * 날짜 유틸리티 함수 (SSOT - Single Source of Truth)
 * @module @ict/shared/utils/date
 *
 * 모든 패키지에서 이 모듈의 함수를 사용해야 합니다.
 * DRY 원칙: 날짜 포맷팅 로직 중복 금지
 */

// ============================================================================
// 타입 정의
// ============================================================================

type DateInput = Date | string | null | undefined;

// ============================================================================
// 내부 헬퍼 함수
// ============================================================================

/**
 * DateInput을 Date 객체로 변환
 * @internal
 */
function toDate(date: DateInput): Date | null {
  if (!date) return null;

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return isNaN(dateObj.getTime()) ? null : dateObj;
  } catch {
    return null;
  }
}

/**
 * 날짜 컴포넌트 추출
 * @internal
 */
function getDateParts(date: Date) {
  return {
    year: date.getFullYear(),
    month: String(date.getMonth() + 1).padStart(2, "0"),
    day: String(date.getDate()).padStart(2, "0"),
    hour: String(date.getHours()).padStart(2, "0"),
    minute: String(date.getMinutes()).padStart(2, "0"),
    second: String(date.getSeconds()).padStart(2, "0"),
  };
}

/**
 * UTC 기준 날짜 컴포넌트 추출
 * @internal
 */
function getUtcDateParts(date: Date) {
  return {
    year: date.getUTCFullYear(),
    month: String(date.getUTCMonth() + 1).padStart(2, "0"),
    day: String(date.getUTCDate()).padStart(2, "0"),
    hour: String(date.getUTCHours()).padStart(2, "0"),
    minute: String(date.getUTCMinutes()).padStart(2, "0"),
    second: String(date.getUTCSeconds()).padStart(2, "0"),
  };
}

// ============================================================================
// 날짜 포맷팅 함수
// ============================================================================

/**
 * 날짜를 한국어 형식으로 포맷팅 (YYYY년 MM월 DD일)
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024년 1월 15일" 형식, 유효하지 않으면 "-"
 *
 * @example
 * formatDate("2024-01-15") // "2024년 1월 15일"
 * formatDate(new Date()) // "2024년 1월 15일"
 * formatDate(null) // "-"
 */
export function formatDate(date: DateInput): string {
  const dateObj = toDate(date);
  if (!dateObj) return "-";

  return dateObj.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * 날짜+시간을 한국어 형식으로 포맷팅 (YYYY. MM. DD. HH:MM)
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024. 01. 15. 14:30" 형식, 유효하지 않으면 "-"
 *
 * @example
 * formatDateTime("2024-01-15T14:30:00Z") // "2024. 01. 15. 14:30"
 */
export function formatDateTime(date: DateInput): string {
  const dateObj = toDate(date);
  if (!dateObj) return "-";

  return dateObj.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * 날짜를 HTML input[type="date"]용 YYYY-MM-DD 형식으로 포맷팅 (UTC 기준)
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024-01-15" 형식, 유효하지 않으면 ""
 *
 * @example
 * formatDateForInput("2024-01-15T14:30:00Z") // "2024-01-15"
 * formatDateForInput(new Date()) // "2024-01-15"
 */
export function formatDateForInput(date: DateInput): string {
  // 이미 YYYY-MM-DD 형식이면 그대로 반환
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const dateObj = toDate(date);
  if (!dateObj) return "";

  const { year, month, day } = getUtcDateParts(dateObj);
  return `${year}-${month}-${day}`;
}

/**
 * 날짜를 YYYY-MM-DD 형식으로 포맷팅 (짧은 형식, UTC 기준)
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024-01-15" 형식, 유효하지 않으면 "-"
 *
 * @example
 * formatDateShort("2024-01-15T14:30:00Z") // "2024-01-15"
 */
export function formatDateShort(date: DateInput): string {
  const dateObj = toDate(date);
  if (!dateObj) return "-";

  const { year, month, day } = getUtcDateParts(dateObj);
  return `${year}-${month}-${day}`;
}

/**
 * 날짜를 YYYY. MM. DD 형식으로 포맷팅 (점 구분)
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024. 01. 15" 형식, 유효하지 않으면 ""
 *
 * @example
 * formatDateDot("2024-01-15") // "2024. 01. 15"
 */
export function formatDateDot(date: DateInput): string {
  const dateObj = toDate(date);
  if (!dateObj) return "";

  const { year, month, day } = getDateParts(dateObj);
  return `${year}. ${month}. ${day}`;
}

/**
 * 날짜를 YYYY-MM-DD 형식으로 포맷팅 (대시 구분, UTC 기준)
 * formatDateShort과 동일하나 빈 문자열 반환 (DOCX용)
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024-01-15" 형식, 유효하지 않으면 ""
 */
export function formatDateDash(date: DateInput): string {
  const dateObj = toDate(date);
  if (!dateObj) return "";

  const { year, month, day } = getUtcDateParts(dateObj);
  return `${year}-${month}-${day}`;
}

/**
 * 날짜를 YYYY년 MM월 DD일 형식으로 포맷팅 (한글)
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024년 01월 15일" 형식, 유효하지 않으면 ""
 */
export function formatDateKorean(date: DateInput): string {
  const dateObj = toDate(date);
  if (!dateObj) return "";

  const { year, month, day } = getDateParts(dateObj);
  return `${year}년 ${month}월 ${day}일`;
}

/**
 * 날짜를 서명용 공백 포함 한글 형식으로 포맷팅
 * @param date - Date 객체 또는 날짜 문자열
 * @returns "2024년      01월      15일" 형식
 */
export function formatDateKoreanSpaced(date: DateInput): string {
  const dateObj = toDate(date);
  if (!dateObj) return "";

  const { year, month, day } = getDateParts(dateObj);
  return `${year}년      ${month}월      ${day}일`;
}

// ============================================================================
// 날짜 생성 함수
// ============================================================================

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 * @returns "2024-01-15" 형식
 */
export function getTodayDateString(): string {
  const today = new Date();
  const { year, month, day } = getDateParts(today);
  return `${year}-${month}-${day}`;
}

/**
 * 현재 연도를 문자열로 반환
 * @returns "2024"
 */
export function getCurrentYear(): string {
  return String(new Date().getFullYear());
}

/**
 * 현재 날짜를 YYYY. MM. DD 형식으로 반환
 * @returns "2024. 01. 15"
 */
export function getCurrentDateDot(): string {
  return formatDateDot(new Date());
}

/**
 * 오늘 날짜 기반 문서번호 생성 (YYYYMMDD-001 형식)
 * @returns "20240115-001" 형식
 */
export function generateDocumentNo(): string {
  const today = new Date();
  const { year, month, day } = getDateParts(today);
  return `${year}${month}${day}-001`;
}

// ============================================================================
// 날짜 유효성 검사
// ============================================================================

/**
 * 날짜 유효성 검사
 * @param date - Date 객체 또는 날짜 문자열
 * @returns 유효한 날짜인지 여부
 */
export function isValidDate(date: DateInput): boolean {
  return toDate(date) !== null;
}

// ============================================================================
// 날짜 계산 함수
// ============================================================================

/**
 * 두 날짜 사이의 일수 차이
 * @param endDate - 종료일
 * @param startDate - 시작일
 * @returns 일수 차이 (종료일 - 시작일)
 */
export function diffInDays(endDate: DateInput, startDate: DateInput): number {
  const end = toDate(endDate);
  const start = toDate(startDate);

  if (!end || !start) return 0;

  const diffTime = end.getTime() - start.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * 두 날짜 사이의 연수 차이 (소수점 포함)
 * @param endDate - 종료일
 * @param startDate - 시작일
 * @param decimalPlaces - 소수점 자릿수 (기본: 2)
 * @returns 연수 차이 문자열
 */
export function diffInYearsDecimal(
  endDate: DateInput,
  startDate: DateInput,
  decimalPlaces: number = 2
): string {
  const days = diffInDays(endDate, startDate);
  const years = days / 365;
  return years.toFixed(decimalPlaces);
}
